import React, { useState, useEffect } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, CircularProgress
} from '@mui/material';
import { BodyWeightForm } from './BodyWeightForm';
import { useLanguage } from '../../i18n/LanguageContext';
import { commonDialogStyles } from '../common/dialogStyles';

interface AddBodyWeightDialogProps {
  open: boolean;
  onClose: () => void;
  currentInitialWeight: string; // Used to prefill the form and determine dialog title
  currentInitialFatPercentage: string; // Used to prefill the form
  currentInitialWaistCircumference: string; // Used to prefill the form
  onSave: (data: { weight: string; fatPercentage: string; waistCircumference: string }) => Promise<boolean>;
  isSaving: boolean;
  saveError: string | null;
  selectedDate: Date | null; // For display in DialogTitle
}

export const AddBodyWeightDialog: React.FC<AddBodyWeightDialogProps> = ({
  open,
  onClose,
  currentInitialWeight,
  currentInitialFatPercentage,
  currentInitialWaistCircumference,
  onSave,
  isSaving,
  saveError,
  selectedDate,
}) => {
  const { t } = useLanguage();
  const [currentWeightInput, setCurrentWeightInput] = useState(currentInitialWeight);
  const [currentFatPercentageInput, setCurrentFatPercentageInput] = useState(currentInitialFatPercentage);
  const [currentWaistCircumferenceInput, setCurrentWaistCircumferenceInput] = useState(currentInitialWaistCircumference);

  useEffect(() => {
    // When the dialog is opened, or currentInitialWeight prop changes,
    // reset the internal form state.
    if (open) {
      setCurrentWeightInput(currentInitialWeight);
      setCurrentFatPercentageInput(currentInitialFatPercentage);
      setCurrentWaistCircumferenceInput(currentInitialWaistCircumference);
    }
  }, [currentInitialWeight, currentInitialFatPercentage, currentInitialWaistCircumference, open]);

  const handleSaveInternal = async () => {
    if (currentWeightInput.trim() !== '') {
      await onSave({ 
        weight: currentWeightInput.trim(),
        fatPercentage: currentFatPercentageInput.trim(),
        waistCircumference: currentWaistCircumferenceInput.trim()
      });
      // Parent will handle closing the dialog if save is successful
      // by checking the 'success' boolean and calling 'onClose'.
    }
  };

  // The main Typography display and the trigger Button are removed from this component.
  // It now only renders the Dialog.

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" sx={commonDialogStyles}>
      <DialogTitle>
        {currentInitialWeight ? t('bodyweight.updateTitle') : t('bodyweight.logTitle')}
        {selectedDate && ` - ${selectedDate.toLocaleDateString()}`}
      </DialogTitle>
      <DialogContent sx={{ pt: '10px !important' }}>
        <BodyWeightForm
          weightValue={currentWeightInput}
          fatPercentageValue={currentFatPercentageInput}
          waistCircumferenceValue={currentWaistCircumferenceInput}
          onWeightChange={setCurrentWeightInput}
          onFatPercentageChange={setCurrentFatPercentageInput}
          onWaistCircumferenceChange={setCurrentWaistCircumferenceInput}
          disabled={isSaving}
        />
        {saveError && (
          <Typography color="error" sx={{ mt: 1, textAlign: 'center' }}>
            {saveError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>{t('workout.cancel')}</Button>
        <Button 
          onClick={handleSaveInternal} 
          color="primary" 
          variant="contained" 
          disabled={isSaving || currentWeightInput.trim() === '' || (currentWeightInput.trim() === currentInitialWeight && currentFatPercentageInput.trim() === currentInitialFatPercentage && currentWaistCircumferenceInput.trim() === currentInitialWaistCircumference)}
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {t('workout.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 